import { Component, EventEmitter, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { ICustomer, ILocation, ICompanyFilter } from 'src/app/features/customers/models/customers.models';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CustomersFiltersComponent } from 'src/app/features/customers/components/customers-filters/customers-filters.component';
import { refillCompanyFilter, parseCompanyFilterRequest } from 'src/app/shared/utils/helpers';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { SetCompanyFilter } from 'src/app/features/customers/store/customers.actions';
import * as catalogsActions from 'src/app/features/catalogs/store/catalogs.actions';
import { IPagination } from 'src/app/shared/models/page.model';

@Component({
  selector: 'app-agent-companies-list',
  templateUrl: './agent-companies-list.component.html',
  styleUrls: ['./agent-companies-list.component.scss'],
})
export class AgentCompaniesListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() subsidiaryId: number | null = null;
  @Input() initialLocations: number[] = [];
  @Input() source: 'agents' | 'catalogs' = 'agents';
  @Output() selectedLocationsChange = new EventEmitter<number[]>();
  @Output() companiesSelected = new EventEmitter<ICustomer[]>();

  customers: ICustomer[] = [];
  filteredCompanies: ICustomer[] = [];
  expandedCompanies: number[] = [];
  selectedLocations: number[] = [];
  searchInput = new FormControl('');
  filter: ICompanyFilter = refillCompanyFilter();
  destroy$ = new Subject<void>();

  // Pagination state
  currentPage = 1;
  pageSize = 20;
  isLoadingMore = false;
  hasMorePages = true;
  metadata: IPagination | null = null;
  private scrollSubject = new Subject<Event>();
  private scrollThreshold = 0.9;
  private cooldownPeriod = 500;
  private lastScrollTime = 0;

  availableCompanies$ = this.store.select((state) => state.catalogs.availableCompanies);
  availableCompaniesMetadata$ = this.store.select((state) => state.catalogs.availableCompaniesMetadata);

  constructor(
    private dialog: MatDialog,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.initializeSelection();
    this.setupScrollListener();
    this.setupSearchListener();
    this.setupStateSubscriptions();
    this.loadCompaniesPage(1);
  }

  private setupStateSubscriptions(): void {
    // Subscribe to available companies from store
    this.availableCompanies$.pipe(takeUntil(this.destroy$)).subscribe((companies) => {
      if (companies) {
        this.handleLoadedCompanies(companies);
      }
    });

    // Subscribe to available companies metadata for pagination
    this.availableCompaniesMetadata$.pipe(takeUntil(this.destroy$)).subscribe((metadata) => {
      if (metadata) {
        this.metadata = metadata;
        this.currentPage = metadata.page || 1;
        this.hasMorePages = metadata.page < metadata.pages;
      }
      this.isLoadingMore = false;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialLocations'] && !changes['initialLocations'].firstChange) {
      const newValues = changes['initialLocations'].currentValue;
      if (newValues) {
        this.selectedLocations = newValues.map((id: any) => Number(id));
      }
    }
  }

  private initializeSelection(): void {
    this.selectedLocations = (this.initialLocations || []).map(id => Number(id));
  }

  private setupScrollListener(): void {
    this.scrollSubject
      .pipe(
        debounceTime(100),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => this.checkAndLoadMore(event));
  }

  private setupSearchListener(): void {
    this.searchInput.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(value => this.search(value ?? ''));
  }

  loadCompaniesPage(page: number): void {
    if (this.isLoadingMore) return;

    this.isLoadingMore = true;
    const searchTerm = this.searchInput.value || undefined;
    const filterRequest = parseCompanyFilterRequest(this.filter);
    const hasFilters = filterRequest && filterRequest.filters && filterRequest.filters.length > 0;

    if (page === 1) {
      // Initial load - dispatch GetAvailableCompanies action
      this.store.dispatch(catalogsActions.GetAvailableCompanies({
        page,
        limit: this.pageSize,
        search: searchTerm,
        sortBy: 'name',
        sortMode: 'asc',
        subsidiary_id: this.subsidiaryId!,
        filters: hasFilters ? filterRequest : undefined
      }));
    } else {
      // Load more - dispatch LoadMoreAvailableCompanies action
      this.store.dispatch(catalogsActions.LoadMoreAvailableCompanies({
        page,
        limit: this.pageSize,
        search: searchTerm,
        sortBy: 'name',
        sortMode: 'asc',
        subsidiary_id: this.subsidiaryId!,
        filters: hasFilters ? filterRequest : undefined
      }));
    }
  }

  private handleLoadedCompanies(companies: ICustomer[]): void {
    // State already handles append logic via reducers
    const processedCompanies = this.processCompanies(companies);
    this.customers = processedCompanies;
    this.updateSelectedCompaniesMap(processedCompanies);
    this.filteredCompanies = this.customers;
  }

  private processCompanies(companies: ICustomer[]): ICustomer[] {
    return companies
      .filter((customer: ICustomer) => customer.locations && customer.locations.length > 0)
      .map((customer: ICustomer) => ({
        ...customer,
        id: Number(customer.id),
        locations: [...customer.locations!].map(l => ({ ...l, id: Number(l.id) })).sort(this.sortLocations),
      }));
  }

  private sortLocations(a: ILocation, b: ILocation): number {
    if (a.is_main && !b.is_main) return -1;
    if (!a.is_main && b.is_main) return 1;
    return a.name.localeCompare(b.name);
  }

  private updateSelectedCompaniesMap(companies: ICustomer[]): void {
    companies.forEach((company: ICustomer) => {
      const hasAssignedLocation = company.locations?.some((l: ILocation) =>
        this.selectedLocations.includes(Number(l.id))
      );
      if (hasAssignedLocation) {
        this.selectedCompaniesMap.set(company.id!, company);
      }
    });
  }

  trackByCompany(_index: number, item: ICustomer): number {
    return item.id;
  }

  trackByLocation(_index: number, item: ILocation): number {
    return item.id!;
  }

  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  private checkAndLoadMore(event: Event): void {
    const now = Date.now();

    if (now - this.lastScrollTime < this.cooldownPeriod) return;
    if (this.isLoadingMore) return;
    if (!this.hasMorePages) return;

    const target = event.target as HTMLElement;
    if (!target) return;

    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    const scrollPosition = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPosition > this.scrollThreshold) {
      this.lastScrollTime = now;
      this.loadCompaniesPage(this.currentPage + 1);
    }
  }

  search(_key: string) {
    this.currentPage = 1;
    this.customers = [];
    this.filteredCompanies = [];
    this.hasMorePages = true;
    this.loadCompaniesPage(1);
  }

  openFilter() {
    this.store.dispatch(
      SetCompanyFilter({ keyword: this.searchInput.value ?? '', advanced: this.filter })
    );

    const dialogRef = this.dialog.open(CustomersFiltersComponent, {
      minWidth: '650px',
      width: '80vw',
      height: '80vh',
    });

    dialogRef.afterClosed().subscribe((filter: ICompanyFilter) => {
      if (filter) {
        this.filter = filter;
        this.store.dispatch(
          SetCompanyFilter({ keyword: this.searchInput.value ?? '', advanced: filter })
        );
        this.currentPage = 1;
        this.customers = [];
        this.filteredCompanies = [];
        this.hasMorePages = true;
        this.loadCompaniesPage(1);
      }
    });
  }

  clearFilter() {
    this.filter = refillCompanyFilter();
    this.store.dispatch(
      SetCompanyFilter({ keyword: this.searchInput.value ?? '', advanced: this.filter })
    );
    this.currentPage = 1;
    this.customers = [];
    this.filteredCompanies = [];
    this.hasMorePages = true;
    this.loadCompaniesPage(1);
  }

  isCompanyExpanded(company: ICustomer): boolean {
    return this.expandedCompanies.includes(company.id);
  }

  isLocationSelected(locationId: number | undefined): boolean {
    if (!locationId) return false;
    return this.selectedLocations.includes(Number(locationId));
  }

  toggleCompanyExpand(company: ICustomer): void {
    const index = this.expandedCompanies.indexOf(company.id);
    if (index > -1) {
      this.expandedCompanies.splice(index, 1);
    } else {
      this.expandedCompanies.push(company.id);
    }
  }

  isAllLocationsSelected(): boolean {
    const allLocationIds = this.filteredCompanies.flatMap(
      (company: ICustomer) => company.locations?.map((loc: ILocation) => loc.id) || []
    );
    return allLocationIds.length
      ? allLocationIds.every((id: any) => this.selectedLocations.includes(id))
      : false;
  }

  isPartialSelectedAll(): boolean {
    const allLocationIds = this.filteredCompanies.flatMap(
      (company: ICustomer) => company.locations?.map((loc: ILocation) => loc.id) || []
    );
    const selectedCount = allLocationIds.filter((id: any) =>
      this.selectedLocations.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < allLocationIds.length;
  }

  isCompanyFullySelected(companyId: number): boolean {
    const company = this.filteredCompanies.find((c: ICustomer) => c.id === companyId);
    if (!company || !company.locations?.length) return false;
    return company.locations.every((loc: ILocation) =>
      this.selectedLocations.includes(loc.id!)
    );
  }

  isCompanyPartiallySelected(companyId: number): boolean {
    const company = this.filteredCompanies.find((c: ICustomer) => c.id === companyId);
    if (!company) return false;
    const locationIds = company.locations?.map((loc: ILocation) => loc.id) || [];
    const selectedCount = locationIds.filter((id: any) =>
      this.selectedLocations.includes(id)
    ).length;
    return selectedCount > 0 && selectedCount < locationIds.length;
  }

  // Track selected company objects to persist them across search/pagination
  selectedCompaniesMap: Map<number, ICustomer> = new Map();

  toggleAllLocations(checked: boolean) {
    const allIds = this.filteredCompanies.flatMap((c: ICustomer) =>
      c.locations?.map((l: ILocation) => l.id!) || []
    );

    if (checked) {
      this.selectedLocations = [...new Set([...this.selectedLocations, ...allIds])];
      this.filteredCompanies.forEach(c => {
        if (c.locations?.some(l => allIds.includes(l.id!))) {
          this.selectedCompaniesMap.set(c.id!, c);
        }
      });
    } else {
      this.selectedLocations = this.selectedLocations.filter(id => !allIds.includes(id));
      this.filteredCompanies.forEach(c => this.selectedCompaniesMap.delete(c.id!));
    }

    this.selectedLocationsChange.emit(this.selectedLocations);
    this.emitCompaniesSelected();
  }

  toggleCompanyLocations(companyId: number, checked: boolean) {
    const company = this.filteredCompanies.find((c: ICustomer) => c.id === companyId);
    if (!company || !company.locations?.length) return;
    const locationIds = company.locations.map((loc: ILocation) => loc.id!);
    this.selectedLocations = checked
      ? [...new Set([...this.selectedLocations, ...locationIds])]
      : this.selectedLocations.filter((id) => !locationIds.includes(id));

    if (checked) {
      this.selectedCompaniesMap.set(companyId, company);
    } else {
      this.selectedCompaniesMap.delete(companyId);
    }

    this.selectedLocationsChange.emit(this.selectedLocations);
    this.emitCompaniesSelected();
  }

  toggleLocation(locationId: number, checked: boolean) {
    if (checked) {
      this.selectedLocations.push(locationId);
      const company = this.filteredCompanies.find(c => c.locations?.some(l => l.id === locationId));
      if (company) {
        this.selectedCompaniesMap.set(company.id!, company);
      }
    } else {
      this.selectedLocations = this.selectedLocations.filter((id) => id !== locationId);
    }
    this.selectedLocationsChange.emit(this.selectedLocations);
    this.emitCompaniesSelected();
  }

  private buildSelectedCompanies(): ICustomer[] {
    const selectedIds = new Set(this.selectedLocations);
    const allKnownCompanies = new Map(this.selectedCompaniesMap);
    this.customers.forEach(c => allKnownCompanies.set(c.id!, c));

    const result: ICustomer[] = [];

    allKnownCompanies.forEach(company => {
      const selectedLocations = (company.locations || []).filter(
        (loc: ILocation) => loc.id != null && selectedIds.has(loc.id)
      );

      if (selectedLocations.length > 0) {
        result.push({
          ...company,
          nbr_locations: company.locations?.length,
          locations: selectedLocations
        } as ICustomer);
      }
    });

    return result;
  }

  private emitCompaniesSelected(): void {
    const selectedCompanies = this.buildSelectedCompanies();
    this.companiesSelected.emit(selectedCompanies);
  }

  getFiltersCount(): number {
    return parseCompanyFilterRequest(this.filter)?.filters?.length || 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearFilter();
    this.store.dispatch(catalogsActions.ClearAvailableCompanies());
  }
}
